import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/scroll";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function BlogPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [state, setState] = useState("loading");
  usePageMeta(
    post ? `${post.title} | Build Right Partners` : "Insights | Build Right Partners",
    post?.excerpt || "Marketing insights from Build Right Partners."
  );

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
    axios
      .get(`${API}/posts/${id}`)
      .then(({ data }) => {
        setPost(data);
        setState("ok");
      })
      .catch(() => setState("missing"));
  }, [id]);

  return (
    <main data-testid="blog-post-page" className="bg-[#060B18] text-slate-100 min-h-screen">
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 pt-40 pb-28">
        <Link
          data-testid="blog-post-back-link"
          to="/blog"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 hover:text-amber-300 transition-colors"
        >
          <ArrowLeft size={14} /> All Insights
        </Link>

        {state === "loading" && (
          <p className="mt-16 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">Loading…</p>
        )}
        {state === "missing" && (
          <p className="mt-16 font-serif text-3xl italic text-amber-100">This post could not be found.</p>
        )}
        {state === "ok" && post && (
          <>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-10 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              <span className="text-amber-400/90">{post.tag}</span>
              <span className="text-slate-500">
                {post.created_at ? new Date(post.created_at).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : ""}
              </span>
              {post.read_time && <span className="text-slate-600">· {post.read_time}</span>}
            </motion.div>
            <h1 className="mt-6 font-serif font-bold tracking-tight leading-[1.1] text-3xl sm:text-5xl text-amber-50" data-testid="blog-post-title">
              {post.title}
            </h1>
            <p className="mt-6 font-serif italic text-xl text-amber-200/90 leading-relaxed">{post.excerpt}</p>
            <span className="gold-hairline block mt-10" />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-10 space-y-6"
              data-testid="blog-post-content"
            >
              {post.content.split(/\n\s*\n/).map((para, i) => (
                <p key={i} className="text-base font-light leading-relaxed text-slate-300/95">
                  {para}
                </p>
              ))}
            </motion.div>
          </>
        )}
      </article>
      <Footer />
    </main>
  );
}
